import React from 'react';
import { connect } from 'react-redux';
import GenericFormField from '../../../../../generic/GenericFormField';
import ValueEditorView from './ValueEditorView';
import { VALUE_TYPES } from '../../valuePickerUtils';
import { deleteObjectData, newObjectData, setObjectDataContentValue,
    setPropertyFilter, pushBreadcrumb, popBreadcrumb, setActiveBreadcrumb } from '../../../../../../actions/reduxActions/valueEditor';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css'; // TODO - Fix this import somehow ?
import { locateProperty } from '../../../../../../reducers/valueEditor';

const isList = contentType => contentType === 'ContentList';

/**
 * Manage the presentation of ContentObject and ContentList values
 *
 * Values are stored in a hierachical tree structure so we need to recurse down each branch
 * presenting an option to enter a default value for each property.
 *
 * See the valueEditor.js reducer for full details on the data structures and management
 *
 * @param {String}   contentType either ContentObject or ContentList
 * @param {String}   valueEditorId the Id that references our current objectData `valueChunk`. This component provides
 *                                 the ability to edit/view a single chunk of a Value that is basically the objectData
 *                                 within the tree of objects.
 *
 * From props,
 *
 * @param {Object}   valueChunk the complete Value object with all nested objects/properties
 * @param {Array}    typeElements list of all known types
 * @param {Array}    filteredProperties List of properties for the `typeElementId` to limit the number of data entry fields.
 * @param {Function} handleObjectDataContentValue update the default value for this property
 * @param {Function} handleNewObjectData add new object
 * @param {Function} handleDeleteObjectData delete current object
 * @param {Function} handleSetPropertyFilter update the filtered properties for a Type
 * @param {Function} handlePushBreadcrumb add to the breadcrumb trail
 * @param {Function} handleSetActiveBreadcrumb update the breadcrumb trail with the current active item
 */
export const ValueEditorComplex = ({ contentType, valueEditorId,
    valueChunk, typeElements, filteredProperties,
    handleObjectDataContentValue, handleNewObjectData, handleDeleteObjectData, handleSetPropertyFilter,
    handlePushBreadcrumb, handleSetActiveBreadcrumb }) => {


    const valueType = typeElements.find(t => t.id === valueChunk.typeElementId);

    if (valueChunk.objectData && valueType) {

        const rows = valueChunk.objectData.map((listItem) => {

            // Iterate over each (possibly filtered) property for this Type because the objectData may be sparsely populated.
            const fields = (filteredProperties.length ? filteredProperties : valueType.properties).map((propertyType) => {

                if (propertyType.typeElementId && VALUE_TYPES.find(t => t.value === propertyType.contentType && t.requiresType)) {
                    // Nested Type so traverse down the hierarchy one level
                    const childObjectData = listItem.properties.find(cod => cod.typeElementId === propertyType.typeElementId);
                    const typeDefinition = typeElements.find(t => t.id === propertyType.typeElementId);

                    if (!childObjectData.objectData) {

                        return {
                            key: `editor_${listItem.internalId}_${propertyType.id}`,
                            name: propertyType.developerName,
                            component: (
                                <div className="new-child-value form-group">
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-nobackground"
                                        onClick={() => {
                                            handleNewObjectData(childObjectData.valueEditorId, typeDefinition);
                                            handlePushBreadcrumb(childObjectData.valueEditorId,
                                                typeDefinition.developerName,
                                                () => handleSetActiveBreadcrumb(childObjectData.valueEditorId));
                                        }}
                                        title={`Add new ${typeDefinition.developerName}`}
                                    >
                                        <span className="glyphicon glyphicon-plus" />
                                    </button>
                                </div>
                            ),
                        };
                    }

                    return {
                        key: `editor_${listItem.internalId}_${propertyType.id}`,
                        name: propertyType.developerName,
                        component: (
                            <div className="edit-child-value form-group">
                                <button
                                    type="button"
                                    className="btn btn-primary btn-nobackground"
                                    onClick={() => handlePushBreadcrumb(childObjectData.valueEditorId,
                                        typeDefinition.developerName,
                                        () => handleSetActiveBreadcrumb(childObjectData.valueEditorId))}
                                    title={`Edit ${propertyType.developerName}`}
                                >
                                    <span className="glyphicon glyphicon-edit" />
                                </button>
                            </div>
                        ),
                    };
                }

                // Single property of an Object
                //
                // If any default values have been saved for this Object populate the display field.
                const property = listItem.properties.find(prop => prop.typeElementPropertyId === propertyType.id);

                // The React key must not use the array index because the index mutates
                return {
                    key: `${listItem.internalId}_${propertyType.id}`,
                    name: propertyType.developerName,
                    component: (
                        <GenericFormField
                            value={property.contentValue || ''}
                            format={propertyType.contentFormat}
                            type={propertyType.contentType}
                            onChange={e => handleObjectDataContentValue(e.target, property.valueEditorId)}
                            isRequired={false}
                        />
                    ),
                };
            });

            // Option to delete existing default values.
            const deleteButton = {
                key: `del_${listItem.internalId}`,
                name: listItem.developerName,
                component: (
                    <button
                        type="button"
                        className="btn btn-danger btn-nobackground"
                        onClick={() => handleDeleteObjectData(valueEditorId, listItem.internalId)}
                        title={`Delete ${listItem.developerName}`}
                    >
                        <span className="glyphicon glyphicon-trash" />
                    </button>
                ),
            };

            return { key: listItem.internalId, fields, deleteButton, typeName: valueType.developerName };
        });

        /*
        * We now have all our rows and fields ready to render
        */

        let extraBtn;
        if (isList(contentType)) {
            // For a List present a 'Add new row' button
            extraBtn = (
                <div key={valueType.id} className="add-row">
                    <button
                        type="button"
                        className="btn btn-primary btn-nobackground"
                        onClick={() => handleNewObjectData(valueEditorId, valueType)}
                        title={`Add new ${valueType.developerName}`}
                    >
                        <span className="glyphicon glyphicon-plus" />
                    </button>
                </div>
            );
        }

        return (
            <React.Fragment>
                <div className="row form-group">
                    { extraBtn ? (
                        <div className="col-md-1">
                            {extraBtn}
                        </div>)
                        : null
                    }
                    <div className={`col-md-${extraBtn ? 11 : 12}`}>
                        <Typeahead
                            id="value-editor-filter"
                            onChange={selected => handleSetPropertyFilter(selected, valueType.id)}
                            labelKey="developerName"
                            multiple
                            options={valueType.properties}
                            placeholder="Filter properties"
                            selected={filteredProperties}
                            clearButton
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <ValueEditorView rows={rows} />
                    </div>
                </div>
            </React.Fragment>
        );
    }

    // No data, so allow user to create new default values
    return (
        <div className="row col-md-12">
            <div className="new-value form-group">
                <button
                    type="button"
                    className="btn btn-primary btn-nobackground"
                    onClick={() => {
                        handleNewObjectData(valueChunk.valueEditorId, valueType);
                        handlePushBreadcrumb(valueChunk.valueEditorId,
                            valueType.developerName,
                            () => handleSetActiveBreadcrumb(valueChunk.valueEditorId));
                    }}
                    title={`Add new ${valueType.developerName}`}
                >
                    <span className="glyphicon glyphicon-plus" />
                </button>
            </div>
        </div>
    );
};


const mapDispatchToProps = {
    handleObjectDataContentValue: setObjectDataContentValue,
    handleNewObjectData: newObjectData,
    handleDeleteObjectData: deleteObjectData,
    handleSetPropertyFilter: setPropertyFilter,
    handlePushBreadcrumb: pushBreadcrumb,
    handlePopBreadcrumb: popBreadcrumb,
    handleSetActiveBreadcrumb: setActiveBreadcrumb,
};

// Exported for testing only.
export const mapStateToProps = (state, ownProps) => {

    // Find our value 'chunk'
    const valueChunk = locateProperty(state.valueEditor.value, ownProps.valueEditorId);

    // Types with many properties can be cumbersome to manage, especially when you are only interested in a few
    // of the properties. Apply any filtering to limit the list properties and make it easier to fill in just a few fields.
    const valueType = state.typeElements.find(t => t.id === valueChunk.typeElementId);
    let filteredProperties = [];
    if (valueType && state.valueEditor.filteredProperties[valueChunk.typeElementId] !== undefined) {
        filteredProperties = state.valueEditor.filteredProperties[valueChunk.typeElementId];
    }

    return { filteredProperties, typeElements: state.typeElements, valueChunk };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(ValueEditorComplex);
